/** Reset before measuring detail tiers so growing the viewport restores full size. */
export function resetPipScale(root: HTMLElement): void {
  for (const property of ['width', 'height', 'max-height', 'transform', 'transform-origin']) root.style.removeProperty(property)
  delete root.dataset.scale
  delete root.dataset.fitFailed
}

/** Last resort after hiding secondary data. The host remains the real viewport;
 * expand an unscaled layout canvas, then fit that canvas back into the host.
 * This imposes no minimum viewport size and preserves complete text and controls.
 */
export function fitPipScale(root: HTMLElement, fits: (root: HTMLElement) => boolean, maxExpansion = Infinity): number {
  delete root.dataset.fitFailed
  if (fits(root)) return 1
  const failed = () => {
    resetPipScale(root)
    root.dataset.fitFailed = 'true'
    return 1
  }
  const measure = (scale: number) => {
    // Composition slots cap the resting HUD at 100%. The temporary logical
    // canvas must be allowed to grow vertically before it is scaled back down.
    root.style.maxHeight = 'none'
    root.style.width = `${100 / scale}%`
    root.style.height = `${100 / scale}%`
    return fits(root)
  }
  let upper = 1
  const minimum = 1 / Math.max(1, maxExpansion)
  let lower = Math.max(.5, minimum)
  // Bounded work even if a broken asset or external stylesheet never fits.
  let lowerFits = measure(lower)
  for (let attempt = 0; attempt < 24 && !lowerFits; attempt++) {
    if (lower <= minimum) break
    upper = lower
    lower = Math.max(lower / 2, minimum)
    lowerFits = measure(lower)
  }
  // Binary search needs a verified fitting endpoint. Font/line-box overflow
  // may never improve as the canvas grows; do not turn that into an empty HUD.
  if (!lowerFits) return failed()
  for (let attempt = 0; attempt < 8; attempt++) {
    const middle = (lower + upper) / 2
    if (measure(middle)) lower = middle
    else upper = middle
  }
  if (!measure(lower)) return failed()
  root.style.transformOrigin = 'top left'
  root.style.transform = `scale(${lower})`
  root.dataset.scale = String(lower)
  return lower
}
